import { Photo } from '@/types';
import { X, Copy, Star, Calendar, HardDrive, FileImage, Lock, Camera, Aperture, Gauge, Clock, MapPin, Zap, Plus, Folder as FolderIcon, Heart, Code, User, Palette, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TagSelector } from '@/components/TagSelector';
import { FolderSelector } from '@/components/FolderSelector';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';

interface DetailPanelProps {
  photo: Photo;
  onClose: () => void;
  onPhotoUpdate?: (photo: Photo) => void;
}

// Pastel colors that work in both light and dark mode
const PASTEL_COLORS = [
  'hsl(262 70% 70%)', // purple
  'hsl(330 70% 75%)', // pink
  'hsl(217 70% 75%)', // blue
  'hsl(173 60% 60%)', // teal
  'hsl(142 50% 60%)', // green
  'hsl(48 80% 70%)', // yellow
  'hsl(25 80% 70%)', // orange
  'hsl(0 70% 75%)', // red
];

const getRandomPastelColor = (seed: string) => {
  // Use string hash for consistent color assignment
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PASTEL_COLORS[Math.abs(hash) % PASTEL_COLORS.length];
};

export function DetailPanel({ photo, onClose, onPhotoUpdate }: DetailPanelProps) {
  const [description, setDescription] = useState(photo.description);
  const [notes, setNotes] = useState(photo.notes || '');
  const [isFavorite, setIsFavorite] = useState(photo.is_favorite || false);
  const [tags, setTags] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [folders, setFolders] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [isLoadingFolders, setIsLoadingFolders] = useState(true);
  const [exifData, setExifData] = useState<any>(null);
  const [isLoadingExif, setIsLoadingExif] = useState(true);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [availableTags, setAvailableTags] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [availableFolders, setAvailableFolders] = useState<Array<{ id: string; name: string; color?: string }>>([]);

  // Update description, notes, and favorite when photo changes
  useEffect(() => {
    setDescription(photo.description);
    setNotes(photo.notes || '');
    setIsFavorite(photo.is_favorite || false);
  }, [photo.id, photo.description, photo.notes, photo.is_favorite]);

  // Load tags, folders, EXIF data, and organization from database
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingTags(true);
      setIsLoadingFolders(true);
      setIsLoadingExif(true);
      
      // Get organization ID
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userOrgs } = await supabase
          .from('user_organizations')
          .select('organization_id')
          .eq('user_id', user.id)
          .limit(1);
        
        if (userOrgs && userOrgs.length > 0) {
          setOrganizationId(userOrgs[0].organization_id);
          
          // Load available tags for this organization
          const { data: tagsData } = await supabase
            .from('tags')
            .select('id, name, color')
            .eq('organization_id', userOrgs[0].organization_id)
            .order('name');
          
          if (tagsData) {
            setAvailableTags(tagsData);
          }

          // Load available folders for this organization
          const { data: foldersData } = await supabase
            .from('folders')
            .select('id, name, color')
            .eq('organization_id', userOrgs[0].organization_id)
            .order('name');
          
          if (foldersData) {
            setAvailableFolders(foldersData);
          }
        }
      }
      
      // Load tags
      const { data: tagsData } = await supabase
        .from('photo_tags')
        .select('tag:tags(id, name, color)')
        .eq('photo_id', photo.id);

      if (tagsData) {
        const tagList = tagsData
          .map(item => (item.tag as any))
          .filter(Boolean);
        setTags(tagList);
      }
      setIsLoadingTags(false);

      // Load folders
      const { data: foldersData } = await supabase
        .from('photo_folders')
        .select('folder:folders(id, name, color)')
        .eq('photo_id', photo.id);

      if (foldersData) {
        const folderList = foldersData
          .map(item => (item.folder as any))
          .filter(Boolean);
        setFolders(folderList);
      }
      setIsLoadingFolders(false);

      // Load EXIF data
      const { data: photoData } = await supabase
        .from('photos')
        .select('camera_make, camera_model, aperture, iso, shutter_speed, focal_length, date_taken, gps_latitude, gps_longitude, flash_used, has_pii, pii_details, has_code, code_language, has_person, is_illustrated, has_error')
        .eq('id', photo.id)
        .single();

      if (photoData) {
        setExifData(photoData);
      }
      setIsLoadingExif(false);
    };

    loadData();
  }, [photo.id]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDescriptionBlur = async () => {
    if (description === photo.description) return;

    const { error } = await supabase
      .from('photos')
      .update({ description })
      .eq('id', photo.id);

    if (error) {
      toast.error('Failed to update description');
      setDescription(photo.description);
    } else {
      toast.success('Description updated');
      if (onPhotoUpdate) {
        onPhotoUpdate({ ...photo, description });
      }
    }
  };

  const handleNotesBlur = async () => {
    if (notes === (photo.notes || '')) return;

    const { error } = await supabase
      .from('photos')
      .update({ notes })
      .eq('id', photo.id);

    if (error) {
      toast.error('Failed to update notes');
      setNotes(photo.notes || '');
    } else {
      toast.success('Notes updated');
      if (onPhotoUpdate) {
        onPhotoUpdate({ ...photo, notes });
      }
    }
  };

  const handleToggleFavorite = async () => {
    const newFavorite = !isFavorite;
    setIsFavorite(newFavorite);

    const { error } = await supabase
      .from('photos')
      .update({ is_favorite: newFavorite })
      .eq('id', photo.id);

    if (error) {
      toast.error('Failed to update favorite');
      setIsFavorite(isFavorite);
    } else {
      toast.success(newFavorite ? 'Added to favorites' : 'Removed from favorites');
      if (onPhotoUpdate) {
        onPhotoUpdate({ ...photo, is_favorite: newFavorite });
      }
    }
  };

  const handleTagsChange = async (newTags: Array<{ id: string; name: string; color?: string }>) => {
    const currentTagIds = tags.map(t => t.id);
    const newTagIds = newTags.map(t => t.id);

    // Find tags to add and remove
    const tagsToAdd = newTags.filter(t => !currentTagIds.includes(t.id));
    const tagsToRemove = tags.filter(t => !newTagIds.includes(t.id));

    try {
      // Remove tags
      for (const tag of tagsToRemove) {
        const { error } = await supabase
          .from('photo_tags')
          .delete()
          .eq('photo_id', photo.id)
          .eq('tag_id', tag.id);
        
        if (error) throw error;
      }

      // Add tags
      for (const tag of tagsToAdd) {
        const { error } = await supabase
          .from('photo_tags')
          .insert({
            photo_id: photo.id,
            tag_id: tag.id,
          });
        
        if (error) throw error;
      }

      setTags(newTags);
      
      if (tagsToAdd.length > 0 || tagsToRemove.length > 0) {
        toast.success('Tags updated');
      }
    } catch (error) {
      console.error('Error updating tags:', error);
      toast.error('Failed to update tags');
    }
  };

  const loadAvailableTags = async () => {
    if (!organizationId) return;
    
    const { data: tagsData } = await supabase
      .from('tags')
      .select('id, name, color')
      .eq('organization_id', organizationId)
      .order('name');
    
    if (tagsData) {
      setAvailableTags(tagsData);
    }
  };

  const handleFoldersChange = async (newFolders: Array<{ id: string; name: string; color?: string }>) => {
    const currentFolderIds = folders.map(f => f.id);
    const newFolderIds = newFolders.map(f => f.id);

    // Find folders to add and remove
    const foldersToAdd = newFolders.filter(f => !currentFolderIds.includes(f.id));
    const foldersToRemove = folders.filter(f => !newFolderIds.includes(f.id));

    try {
      // Remove folders
      for (const folder of foldersToRemove) {
        const { error } = await supabase
          .from('photo_folders')
          .delete()
          .eq('photo_id', photo.id)
          .eq('folder_id', folder.id);
        
        if (error) throw error;
      }

      // Add folders
      for (const folder of foldersToAdd) {
        const { error } = await supabase
          .from('photo_folders')
          .insert({
            photo_id: photo.id,
            folder_id: folder.id,
          });
        
        if (error) throw error;
      }

      setFolders(newFolders);
      
      if (foldersToAdd.length > 0 || foldersToRemove.length > 0) {
        toast.success('Folders updated');
      }
    } catch (error) {
      console.error('Error updating folders:', error);
      toast.error('Failed to update folders');
    }
  };

  const handleRemoveFolder = async (folderId: string) => {
    const { error } = await supabase
      .from('photo_folders')
      .delete()
      .eq('photo_id', photo.id)
      .eq('folder_id', folderId);

    if (error) {
      toast.error('Failed to remove from folder');
    } else {
      setFolders(folders.filter(f => f.id !== folderId));
      toast.success('Removed from folder');
    }
  };


  return (
    <div className="w-full md:w-80 h-screen bg-card border-l border-border flex flex-col overflow-y-auto">
      <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
        <h2 className="font-semibold">Details</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 p-4 space-y-4">
        {/* Image preview */}
        <div className="relative aspect-square rounded-lg overflow-hidden bg-secondary">
          <img
            src={photo.url}
            alt={photo.filename}
            className="w-full h-full object-cover"
          />
          {exifData?.has_pii && (
            <div className="absolute top-2 right-2 z-10">
              <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded">
                <Lock className="w-4 h-4 text-white" />
              </div>
            </div>
          )}
        </div>

        {/* Title and Favorite */}
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-lg truncate">{photo.filename}</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleFavorite}
            className="h-9 w-9 flex-shrink-0"
          >
            <Heart 
              className={`w-5 h-5 transition-all ${
                isFavorite 
                  ? 'text-[hsl(0_70%_60%)] [&>path]:fill-current [&>path]:stroke-current' 
                  : 'text-muted-foreground hover:text-[hsl(0_70%_60%)]'
              }`}
            />
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="exif">EXIF</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6 mt-4">
            {/* Color palette */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Color Palette</p>
              {photo.colors.length > 0 ? (
                <div className="flex gap-2">
                  {photo.colors.map((color, index) => (
                    <div
                      key={`${color.id}-${index}`}
                      className="w-8 h-8 rounded-full border-2 border-border cursor-pointer hover:scale-110 transition-fast"
                      style={{ backgroundColor: color.hex }}
                      title={color.hex}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No colors detected</p>
              )}
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">Tags</p>
                <TagSelector
                  trigger={
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Tags
                    </Button>
                  }
                  selectedTags={tags}
                  availableTags={availableTags}
                  organizationId={organizationId}
                  onTagsChange={handleTagsChange}
                  onTagCreated={loadAvailableTags}
                  align="start"
                />
              </div>
              {isLoadingTags ? (
                <p className="text-xs text-muted-foreground">Loading tags...</p>
              ) : tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => {
                    const baseColor = tag.color || getRandomPastelColor(tag.name);
                    // Handle both HSL and HEX color formats with 20% opacity for background
                    const transparentBg = baseColor.startsWith('hsl')
                      ? baseColor.replace(')', ' / 0.2)')
                      : baseColor + '33'; // Add 20% alpha to HEX colors
                    const borderColor = baseColor;
                    return (
                      <div
                        key={tag.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium group border-2"
                        style={{ 
                          backgroundColor: transparentBg,
                          borderColor: borderColor,
                          color: 'white'
                        }}
                      >
                        <span>{tag.name}</span>
                        <button
                          onClick={() => handleTagsChange(tags.filter(t => t.id !== tag.id))}
                          className="opacity-70 hover:opacity-100 transition-fast"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No tags</p>
              )}
            </div>

            {/* Folders */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">Folders</p>
                <FolderSelector
                  trigger={
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <Plus className="w-4 h-4" />
                    </Button>
                  }
                  selectedFolders={folders}
                  availableFolders={availableFolders}
                  organizationId={organizationId}
                  onFoldersChange={handleFoldersChange}
                  align="end"
                />
              </div>
              {isLoadingFolders ? (
                <p className="text-xs text-muted-foreground">Loading folders...</p>
              ) : folders.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {folders.map((folder) => {
                    const baseColor = folder.color || getRandomPastelColor(folder.name);
                    const transparentBg = baseColor.startsWith('hsl')
                      ? baseColor.replace(')', ' / 0.2)')
                      : baseColor + '33';
                    const borderColor = baseColor;
                    return (
                      <div
                        key={folder.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium group border-2"
                        style={{ 
                          backgroundColor: transparentBg,
                          borderColor: borderColor,
                          color: 'white'
                        }}
                      >
                        <FolderIcon className="w-3.5 h-3.5" />
                        <span>{folder.name}</span>
                        <button
                          onClick={() => handleFoldersChange(folders.filter(f => f.id !== folder.id))}
                          className="opacity-70 hover:opacity-100 transition-fast"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No folders</p>
              )}
            </div>

            {/* Description */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Description</p>
              <textarea
                placeholder="Add notes..."
                className="w-full px-3 py-2 bg-secondary rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={handleDescriptionBlur}
              />
            </div>

            {/* PII Warning */}
            {exifData?.has_pii && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-destructive mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-destructive">Contains PII</p>
                    {exifData.pii_details && (
                      <p className="text-xs text-destructive/80 mt-1">{exifData.pii_details}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Properties */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">Properties</p>
              <div className="space-y-3">
                {/* Rating */}
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Rating
                  </span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 cursor-pointer transition-fast ${
                          star <= (photo.rating || 0)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-muted-foreground hover:text-yellow-400'
                        }`}
                        onClick={async () => {
                          const newRating = star === photo.rating ? 0 : star;
                          const { error } = await supabase
                            .from('photos')
                            .update({ rating: newRating })
                            .eq('id', photo.id);

                          if (error) {
                            toast.error('Failed to update rating');
                          } else {
                            toast.success('Rating updated');
                            if (onPhotoUpdate) {
                              onPhotoUpdate({ ...photo, rating: newRating });
                            }
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Dimensions */}
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <FileImage className="w-4 h-4" />
                    Dimensions
                  </span>
                  <span className="font-mono">
                    {photo.dimensions.width} × {photo.dimensions.height}
                  </span>
                </div>

                {/* File size */}
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <HardDrive className="w-4 h-4" />
                    Size
                  </span>
                  <span className="font-mono">{formatFileSize(photo.fileSize)}</span>
                </div>

                {/* File type */}
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <FileImage className="w-4 h-4" />
                    Type
                  </span>
                  <span className="uppercase">{photo.fileType.split('/')[1]}</span>
                </div>

                {/* Date taken (from EXIF) */}
                {exifData?.date_taken && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Camera className="w-4 h-4" />
                      Date Taken (EXIF)
                    </span>
                    <span className="text-xs">{formatDate(exifData.date_taken)}</span>
                  </div>
                )}

                {/* Date uploaded */}
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    Date Uploaded
                  </span>
                  <span className="text-xs">{formatDate(photo.uploadDate)}</span>
                </div>

                {/* Date last modified */}
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    Last Modified
                  </span>
                  <span className="text-xs">{formatDate(photo.updatedAt)}</span>
                </div>

                {/* Characteristics - Only shown if true */}
                {(exifData?.has_code || exifData?.has_person || exifData?.is_illustrated || exifData?.has_error) && (
                  <>
                    <div className="col-span-2 border-t pt-3 mt-1">
                      <p className="text-xs text-muted-foreground mb-2">Characteristics</p>
                    </div>

                    {exifData?.has_code && (
                      <div className="flex items-center justify-between text-sm col-span-2">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <Code className="w-4 h-4" />
                          Contains Code
                        </span>
                        <span className="text-xs text-primary">
                          {exifData?.code_language ? exifData.code_language : 'Yes'}
                        </span>
                      </div>
                    )}

                    {exifData?.has_person && (
                      <div className="flex items-center justify-between text-sm col-span-2">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <User className="w-4 h-4" />
                          Contains Person
                        </span>
                        <span className="text-xs text-primary">Yes</span>
                      </div>
                    )}

                    {exifData?.is_illustrated && (
                      <div className="flex items-center justify-between text-sm col-span-2">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <Palette className="w-4 h-4" />
                          Illustrated
                        </span>
                        <span className="text-xs text-primary">Yes</span>
                      </div>
                    )}

                    {exifData?.has_error && (
                      <div className="flex items-center justify-between text-sm col-span-2">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <AlertCircle className="w-4 h-4" />
                          Processing Error
                        </span>
                        <span className="text-xs text-destructive">Yes</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Notes</p>
              <textarea
                placeholder="Add personal notes..."
                className="w-full px-3 py-2 bg-secondary rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                rows={6}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleNotesBlur}
              />
            </div>
          </TabsContent>

          <TabsContent value="exif" className="space-y-4 mt-4">
            {isLoadingExif ? (
              <p className="text-sm text-muted-foreground">Loading EXIF data...</p>
            ) : exifData ? (
              <div className="space-y-4">
                {/* Camera Information */}
                {(exifData.camera_make || exifData.camera_model) && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Camera</p>
                    <div className="space-y-2">
                      {exifData.camera_make && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Camera className="w-4 h-4" />
                            Make
                          </span>
                          <span>{exifData.camera_make}</span>
                        </div>
                      )}
                      {exifData.camera_model && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Camera className="w-4 h-4" />
                            Model
                          </span>
                          <span>{exifData.camera_model}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Exposure Settings */}
                {(exifData.aperture || exifData.iso || exifData.shutter_speed || exifData.focal_length) && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Exposure</p>
                    <div className="space-y-2">
                      {exifData.aperture && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Aperture className="w-4 h-4" />
                            Aperture
                          </span>
                          <span className="font-mono">f/{exifData.aperture}</span>
                        </div>
                      )}
                      {exifData.iso && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Gauge className="w-4 h-4" />
                            ISO
                          </span>
                          <span className="font-mono">{exifData.iso}</span>
                        </div>
                      )}
                      {exifData.shutter_speed && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            Shutter Speed
                          </span>
                          <span className="font-mono">{exifData.shutter_speed}</span>
                        </div>
                      )}
                      {exifData.focal_length && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Camera className="w-4 h-4" />
                            Focal Length
                          </span>
                          <span className="font-mono">{exifData.focal_length}</span>
                        </div>
                      )}
                      {exifData.flash_used !== null && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Zap className="w-4 h-4" />
                            Flash
                          </span>
                          <span>{exifData.flash_used ? 'Yes' : 'No'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Location */}
                {(exifData.gps_latitude || exifData.gps_longitude) && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Location</p>
                    <div className="space-y-2">
                      {exifData.gps_latitude && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            Latitude
                          </span>
                          <span className="font-mono">{exifData.gps_latitude.toFixed(6)}</span>
                        </div>
                      )}
                      {exifData.gps_longitude && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            Longitude
                          </span>
                          <span className="font-mono">{exifData.gps_longitude.toFixed(6)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!exifData.camera_make && !exifData.camera_model && !exifData.aperture && !exifData.iso && !exifData.shutter_speed && !exifData.focal_length && !exifData.gps_latitude && !exifData.gps_longitude && (
                  <p className="text-sm text-muted-foreground">No EXIF data available</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No EXIF data available</p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
